/* ──────────────────────────────────────────────────────────────
 *  Axios client — single configured instance for all API calls
 *
 *  Features:
 *   • Base URL from env (VITE_API_BASE_URL) or fallback /api
 *   • CSRF-safe: withCredentials for same-origin auth cookies
 *   • Request interceptor: attach JSON content-type + auth token
 *   • Response interceptor: normalise errors into a standard shape
 *   • Timeout: 60 s (geometry / compute can be slow)
 * ────────────────────────────────────────────────────────────── */

import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { API_BASE_URL } from '@/constants/app';
import { acquireAccessToken } from '@/auth/tokenHelper';

/* ── Standard error shape returned to callers ─────────────── */

export interface ApiError {
  status: number;
  message: string;
  detail?: unknown;
}

/* ── Create instance ──────────────────────────────────────── */

const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true, // send auth cookies on same-origin
});

/* ── Request interceptor ──────────────────────────────────── */

axiosClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Let Axios set the correct multipart Content-Type (with boundary)
    // when the request body is FormData.
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Acquire a fresh access token from MSAL (silent with cache).
    const token = await acquireAccessToken();

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

/* ── Response interceptor ─────────────────────────────────── */

let authRedirectInFlight = false;

async function redirectToSignIn(): Promise<void> {
  if (authRedirectInFlight) return;

  const { msalInstance } = await import('@/auth/AuthProvider');
  const { loginRequest } = await import('@/auth/msalConfig');

  // No active account means the request was never authenticated to begin with - AuthGate
  // already drives sign-in, and redirecting here would just bounce the page in a loop.
  if (!msalInstance.getActiveAccount()) return;

  authRedirectInFlight = true;
  await msalInstance.acquireTokenRedirect(loginRequest);
}

axiosClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<{ detail?: string; message?: string }>) => {
    const apiError: ApiError = {
      status: error.response?.status ?? 0,
      message:
        error.response?.data?.detail ??
        error.response?.data?.message ??
        error.message ??
        'An unexpected error occurred',
      detail: error.response?.data,
    };

    // 401 — token expired or invalid; force re-authentication
    if (apiError.status === 401) {
      void redirectToSignIn();
    }

    // Surface server-side 5xx to the console for on-call diagnostics.
    if (apiError.status >= 500) {
      // eslint-disable-next-line no-console
      console.error('[OptiBush API]', apiError.status, apiError.message);
    }

    return Promise.reject(apiError);
  },
);

export default axiosClient;
